import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { generatePath, useParams } from 'react-router'
import styled from 'styled-components'
import { Badge, Tooltip } from 'antd'
import { colours } from '@qflow/theme'

import { MODULES } from 'portal/lib/constants'
import { ClickableLink, icons } from 'portal/lib/primitives'
import * as ROUTES from 'portal/routes'
import { useModuleIds } from 'portal/lib/hooks'

export const RecordLink = (setCellId, filters, updateFilters) => {
  const handleResetFilters = () => {
    for (const key in filters) {
      if (typeof filters[key] === 'object' && filters[key].length > 0) {
        updateFilters(key, [])
      } else if (typeof filters[key] === 'string') {
        updateFilters(key, '')
      }
    }
  }

  // If all filters (excluding deliveryDate AND removalDate) are null, empty string, or empty array, disable the reset button
  const allFiltersEmpty = Object.entries(filters).every(([key, filter]) => {
    if (key === 'deliveryDate' || key === 'removalDate') {
      return true
    }
    return (
      filter === null ||
      filter === '' ||
      (Array.isArray(filter) && filter.length === 0)
    )
  })

  // Count the number of active filters (excluding deliveryDate AND removalDate) to display in the badge
  const activeFilterCount = Object.entries(filters).reduce(
    (count, [key, filter]) => {
      if (key === 'deliveryDate' || key === 'removalDate') {
        return count
      }
      if (
        (typeof filter === 'string' && filter !== '') ||
        (Array.isArray(filter) && filter.length > 0)
      ) {
        return count + 1
      }
      return count
    },
    0
  )

  return {
    title: (
      <Tooltip
        title={allFiltersEmpty ? 'No filters to clear' : 'Clear all filters'}
      >
        <ResetFilters
          aria-label={
            allFiltersEmpty ? 'No filters to clear' : 'Clear all filters'
          }
          disabled={allFiltersEmpty}
          onClick={handleResetFilters}
        >
          <Badge
            count={allFiltersEmpty ? 0 : activeFilterCount}
            offset={[-1, 1]}
            color={colours.TEAL}
            styles={{
              root: {
                color: 'inherit'
              }
            }}
          >
            <icons.ResetFilters size="22px" />
          </Badge>
        </ResetFilters>
      </Tooltip>
    ),
    width: '4rem',
    render: (rowData, _, index) => {
      if (
        !rowData.record ||
        !rowData.record.recordId ||
        rowData.record.recordId.length < 3
      ) {
        return ''
      }
      return <RecordLinkRenderer rowData={rowData} record={rowData.record} />
    },
    align: 'right',
    onCell: setCellId
  }
}

/** @param {{accountId: string, projectId: string, moduleName?: string, module: string}} params */
export const getRecordLinkUri = (params, moduleName, recordId) => {
  const route = MODULES.select(moduleName, {
    waste: ROUTES.WASTE_RECORD_DETAILS,
    delivery: ROUTES.DELIVERY_RECORD_DETAILS
  })

  return generatePath(route, {
    ...params,
    recordId: recordId
  })
}

const RecordLinkRenderer = ({ rowData, record }) => {
  const params = useParams()
  const { moduleName } = useModuleIds()
  const { recordId } = record

  const uri = useMemo(() => {
    return getRecordLinkUri(params, moduleName, recordId)
  }, [moduleName, params, recordId])

  return (
    <StyledClickableLink
      id="navigateToDetailsBtn"
      to={uri}
      disabled={rowData.removed}
    >
      {rowData.removed ? (
        <div />
      ) : (
        <Tooltip title="View record">
          <icons.ViewRecord />
        </Tooltip>
      )}
    </StyledClickableLink>
  )
}

RecordLinkRenderer.propTypes = {
  rowData: PropTypes.object.isRequired,
  record: PropTypes.object.isRequired
}

const ResetFilters = styled.button`
  height: 32px;
  width: 32px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  background-color: transparent;

  &:disabled {
    cursor: not-allowed;
    color: ${colours.BATTLESHIP_GREY};
  }
`
const StyledClickableLink = styled(ClickableLink)`
  height: 32px;
  width: 32px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    color: ${colours.TEAL};
  }
`
